import { Component } from 'opus-angular-seed-core';
import { MAX_FILE_SIZE_BYTES, MAX_TRAINING_FILES_SIZE_BYTES, formatFileSizeMb } from '../fileConstants';
@Component({
    bindings: {
        onFileUploaded: '&',
        maxFiles: '<',
        currentFileCount: '<',
        uploadingFiles: '=',
        attachToEntities: '<',
        existingFiles: '<'
    },
    template: `
        <div class="dropzone"
            ng-class="{ 'dropzone--dragover': vm.isDragOver }"
            ngf-drop="vm.onFilesSelected($files)"
            ngf-drag-over-class="'dragover'"
            ngf-multiple="true"
            ng-dragover="vm.onDragOver($event)"
            ng-dragleave="vm.onDragLeave($event)"
            ng-drop="vm.onDrop($event)">
            <input type="file" class="form-control" multiple
                ngf-select="vm.onFilesSelected($files)"/>
            <p class="title">{{ 'file.Drag and drop your file here' | translate }} <small>{{ 'file.or click the button below' | translate }}</small></p>
            <span class="btn dropzone__upload-btn">
                {{ 'file.Choose file to upload' | translate }}
            </span>
        </div>
        <style>
            .dropzone__upload-btn {
                white-space: nowrap;
            }
            .dropzone--dragover {
                background-color: #e8f4fd !important;
                border-color: #2196F3 !important;
                border-style: dashed !important;
                border-width: 2px !important;
            }
            .dropzone.dragover {
                background-color: #e8f4fd !important;
                border-color: #2196F3 !important;
                border-style: dashed !important;
                border-width: 2px !important;
            }
        </style>
    `
})
export default class {
    constructor (FileService, $scope, toastr, $translate) {
        this.FileService = FileService;
        this.$scope = $scope;
        this.toastr = toastr;
        this.$translate = $translate;
        this.isDragOver = false;
        this.allowedExtensions = [
            'xls', 'xlsx', 'doc', 'docx', 'jpg', 'jpeg', 'png',
            'pdf', 'ppt', 'pptx', 'csv', 'mp4', 'mov', 'zip', 'epub'
        ];
    }
    $onInit () {
        if (!this.maxFiles) {
            this.maxFiles = 15;
        }
    }
    onDragOver ($event) {
        if ($event) {
            $event.preventDefault();
        }
        this.isDragOver = true;
    }
    onDragLeave ($event) {
        if ($event) {
            $event.preventDefault();
        }
        this.isDragOver = false;
    }
    onDrop ($event) {
        if ($event) {
            $event.preventDefault();
        }
        this.isDragOver = false;
    }
    onFilesSelected (files) {
        if (!files || !files.length) {
            return;
        }
        const availableSlots = this.maxFiles - (this.currentFileCount || 0);
        if (availableSlots <= 0) {
            this.toastr.error(this.$translate.instant('file.Maximum number of files reached'));
            return;
        }
        const filesToUpload = files.slice(0, availableSlots);
        const acceptedFiles = [];
        let runningTotal = this._getExistingFilesSize() + this._getUploadingFilesSize();
        filesToUpload.forEach(file => {
            if (file.size > MAX_FILE_SIZE_BYTES) {
                const message = `${this.$translate.instant('file.File exceeds the maximum allowed size')} (${formatFileSizeMb(MAX_FILE_SIZE_BYTES)}): ${file.name}`;
                this.uploadingFiles.push({ name: file.name, size: file.size, progress: 0, error: message });
                this.toastr.error(message);
                return;
            }
            if (runningTotal + file.size > MAX_TRAINING_FILES_SIZE_BYTES) {
                const message = `${this.$translate.instant('file.Combined size of training files exceeds the allowed maximum')} (${formatFileSizeMb(MAX_TRAINING_FILES_SIZE_BYTES)}): ${file.name}`;
                this.uploadingFiles.push({ name: file.name, size: file.size, progress: 0, error: message });
                this.toastr.error(message);
                return;
            }
            runningTotal += file.size;
            acceptedFiles.push(file);
        });
        acceptedFiles.forEach(file => this.uploadFile(file));
    }
    _getExistingFilesSize () {
        if (!this.existingFiles || !this.existingFiles.length) {
            return 0;
        }
        return this.existingFiles.reduce((sum, file) => sum + (file.size || 0), 0);
    }
    _getUploadingFilesSize () {
        if (!this.uploadingFiles || !this.uploadingFiles.length) {
            return 0;
        }
        return this.uploadingFiles.reduce((sum, item) => sum + (item.error ? 0 : (item.size || 0)), 0);
    }
    uploadFile (file) {
        const extension = file.name.split('.').pop().toLowerCase();
        if (this.allowedExtensions.indexOf(extension) === -1) {
            const allowedList = this.allowedExtensions.map(ext => `.${ext}`).join(', ');
            this.uploadingFiles.push({
                name: file.name,
                size: file.size,
                progress: 0,
                error: `${this.$translate.instant('file.File type not supported. Allowed extensions')} ${allowedList}`
            });
            this.toastr.error(`${this.$translate.instant('file.File type not supported')}: ${file.name}`);
            return;
        }
        const uploadItem = {
            name: file.name,
            size: file.size,
            progress: 0,
            error: null
        };
        this.uploadingFiles.push(uploadItem);
        this.FileService.upload(file, this.attachToEntities).then(
            (response) => {
                uploadItem.progress = 100;
                this.$scope.$applyAsync(() => {
                    this.removeUploadItem(uploadItem);
                    if (this.onFileUploaded) {
                        this.onFileUploaded({ file: response.data });
                    }
                });
            },
            (error) => {
                let message = this.$translate.instant('file.Upload failed');
                if (error.data) {
                    if (Array.isArray(error.data) && error.data.length && error.data[0].message) {
                        message = error.data[0].message;
                    }
                    else if (error.data.message) {
                        message = error.data.message;
                    }
                }
                uploadItem.error = message;
                this.toastr.error(message);
            },
            (evt) => {
                this.$scope.$applyAsync(() => {
                    uploadItem.progress = Math.min(
                        99, parseInt(100.0 * evt.loaded / evt.total, 10)
                    );
                });
            }
        );
    }
    removeUploadItem (item) {
        const index = this.uploadingFiles.indexOf(item);
        if (index > -1) {
            this.uploadingFiles.splice(index, 1);
        }
    }
}



// WEBPACK FOOTER //
// ./src/app/components/file/fileUpload/index.js